import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

interface Step {
  number: number;
  label: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ steps, currentStep }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 4,
        position: 'relative',
      }}
    >
      {/* Connecting Line */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: '10%',
          right: '10%',
          height: 2,
          bgcolor: 'divider',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          left: '10%',
          width: `${((currentStep - 1) / (steps.length - 1)) * 80}%`,
          height: 2,
          bgcolor: 'primary.main',
          zIndex: 1,
          transition: 'width 0.3s ease',
        }}
      />

      {steps.map((step) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;

        return (
          <Box
            key={step.number}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isCompleted || isCurrent ? 'primary.main' : 'background.paper',
                border: '2px solid',
                borderColor: isCompleted || isCurrent ? 'primary.main' : 'divider',
                color: isCompleted || isCurrent ? 'primary.contrastText' : 'text.secondary',
                fontWeight: 600,
                transition: 'all 0.3s ease',
              }}
            >
              {isCompleted ? (
                <CheckIcon sx={{ fontSize: 20 }} />
              ) : (
                step.number
              )}
            </Box>
            {!isMobile && (
              <Typography
                variant="caption"
                sx={{
                  mt: 1,
                  color: isCurrent ? 'primary.main' : 'text.secondary',
                  fontWeight: isCurrent ? 600 : 400,
                  textAlign: 'center',
                }}
              >
                {step.label}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default StepIndicator;
